/**
 * `site:`-operator parsing for LLM grounding searches.
 *
 * ChatGPT (and increasingly other platforms) emits fan-out queries of the form
 * `site:domain.com <topic>` — the model electing to consult ONE specific site it
 * already trusts rather than searching the open web. Those queries are evidence
 * about authority, never opportunities:
 *
 *   - A SERP task for `site:ourbrand.com …` ranks us #1 by construction, which
 *     flatters every rank metric it touches.
 *   - A SERP task for `site:competitor.com …` returns zero brand results, so the
 *     gap formula scores it a top "high-impact gap" that nothing can ever close.
 *   - Nothing can rank for a query scoped to someone else's domain, so the
 *     citation optimizer's gates are meaningless for one.
 *
 * Everything here is PURE — no database, no network — so it is safe on both the
 * server barrel and `@repo/core/client`.
 */

import {
	type PropertyFocalDomainSource,
	normalizeDomainOrNull,
	propertyFocalDomain,
} from "./domain";

/**
 * The `site:` operator, anchored to start-of-string or whitespace.
 *
 * The anchor is load-bearing twice over:
 *   - `-site:acme.com` is a NEGATION ("exclude this domain"), the opposite of a
 *     scoped query, and the `-` is neither start nor whitespace so it can't match.
 *   - `website:acme.com` / `inurl:` / `intitle:` end with or resemble the token
 *     but are different operators; the same anchor keeps them out.
 *
 * `\s?` admits the spaced variant (`site: acme.com`) that some models emit.
 * Global + case-insensitive: callers walk every occurrence and the first one that
 * parses as a domain wins.
 */
const SITE_OPERATOR = /(^|\s)site:\s?(\S+)/gi;

export interface SiteScopeParseResult {
	/**
	 * The normalized bare hostname the query was scoped to (`acme.com`), or null
	 * when the query carries no usable `site:` operator.
	 */
	domain: string | null;
	/**
	 * The query with every domain-bearing `site:` operator removed and whitespace
	 * collapsed — i.e. the underlying topic the model was actually researching.
	 * Equal to the trimmed input when nothing was scoped, and "" for a bare
	 * `site:acme.com` with no other terms.
	 */
	residualQuery: string;
}

/**
 * Extract the domain a grounding search was scoped to.
 *
 * A captured token counts as a domain only when it survives
 * {@link normalizeDomainOrNull} **and** the result contains a dot. The dot is
 * what separates a real scope from a false positive: `site: acme.com` parses,
 * `site: how to pick a CRM` does not (its first token, "how", is not a domain).
 * Without that test every `site:` followed by an ordinary word would be treated
 * as a scoped query and silently dropped from SERP and recommendations.
 *
 * @example
 * parseSiteScope("site:acme.com pricing")       // { domain: "acme.com", residualQuery: "pricing" }
 * parseSiteScope("site: acme.com pricing")      // { domain: "acme.com", residualQuery: "pricing" }
 * parseSiteScope("site:www.acme.com/docs api")  // { domain: "acme.com", residualQuery: "api" }
 * parseSiteScope("site:acme.com")               // { domain: "acme.com", residualQuery: "" }
 * parseSiteScope("crm -site:acme.com")          // { domain: null, residualQuery: "crm -site:acme.com" }
 * parseSiteScope("website:acme.com crm")        // { domain: null, residualQuery: "website:acme.com crm" }
 * parseSiteScope("site: how to pick a CRM")     // { domain: null, residualQuery: "site: how to pick a CRM" }
 */
export function parseSiteScope(
	query: string | null | undefined,
): SiteScopeParseResult {
	const source = typeof query === "string" ? query : "";
	const trimmed = source.trim();

	if (!trimmed) {
		return { domain: null, residualQuery: "" };
	}

	let domain: string | null = null;
	let residual = "";
	let lastIndex = 0;

	// Fresh regex per call — a module-level /g regex carries `lastIndex` between
	// calls and would skip matches on every other invocation.
	const pattern = new RegExp(SITE_OPERATOR.source, SITE_OPERATOR.flags);

	let match = pattern.exec(trimmed);
	while (match !== null) {
		const [whole, leading = "", token = ""] = match;
		const candidate = normalizeDomainOrNull(token);

		// A domain without a dot is a bare word (or a hostname like "localhost"
		// that can't be a public site scope) — leave it in the residual query.
		if (candidate?.includes(".")) {
			if (domain === null) {
				domain = candidate;
			}
			// Keep the leading whitespace so adjacent words don't fuse together;
			// the collapse below tidies the run.
			residual += trimmed.slice(lastIndex, match.index) + leading;
			lastIndex = match.index + whole.length;
		}

		match = pattern.exec(trimmed);
	}

	if (domain === null) {
		return { domain: null, residualQuery: trimmed };
	}

	residual += trimmed.slice(lastIndex);

	return {
		domain,
		residualQuery: residual.replace(/\s+/g, " ").trim(),
	};
}

/**
 * True when a query is scoped to a specific site with the `site:` operator.
 *
 * The single predicate every exclusion should branch on — SERP submission,
 * gap scoring, recommendations, and the citation optimizer all use it so they
 * can never disagree about what counts as scoped.
 */
export function isSiteScopedQuery(query: string | null | undefined): boolean {
	return parseSiteScope(query).domain !== null;
}

/**
 * The single rejection message for a `site:`-scoped citation-optimizer query.
 *
 * Lives here, not in the API package, because BOTH entry points must say the
 * same thing: the HTTP route rejects at the Zod boundary, and the MCP
 * `score_citation_pipeline` tool calls `createCitationRun` in core directly and
 * never sees that schema.
 */
export const SITE_SCOPED_QUERY_MESSAGE =
	"site:-scoped queries can't be scored — they tell the AI to consult one specific site instead of searching the open web. Enter the underlying topic query without the site: operator.";

// ---------------------------------------------------------------------------
// Classification — whose site did the model choose to consult?
// ---------------------------------------------------------------------------

/** Which camp a scoped domain belongs to, from the property's point of view. */
export type SiteScopeClass = "own" | "competitor" | "third_party";

export interface SiteScopeClassification {
	class: SiteScopeClass;
	/** Set only for `class === "competitor"`. */
	competitorId?: string;
	/** Set only for `class === "competitor"`. */
	competitorName?: string;
}

/** The competitor shape the classifier needs — a subset of `Competitor`. */
export interface SiteScopeCompetitorSource {
	id: string;
	name: string | null;
	url: string;
}

export interface SiteScopeClassifierContext {
	property: PropertyFocalDomainSource & {
		/**
		 * Legitimate non-canonical domains (legacy TLDs, ccTLDs, redirect hosts)
		 * that should classify as "own". Optional: the Property column that feeds
		 * this arrives in a later change, so every current caller passes nothing
		 * and gets focal-domain-only behavior.
		 */
		additionalDomains?: string[] | null;
	};
	competitors: SiteScopeCompetitorSource[];
}

/** A classified domain function. Cheap to call — all matching is precomputed. */
export type SiteScopeClassifier = (
	domain: string | null | undefined,
) => SiteScopeClassification;

const THIRD_PARTY: SiteScopeClassification = { class: "third_party" };

/**
 * True when `host` is `target` or a subdomain of it.
 *
 * Both sides are expected to be already-normalized bare hostnames. Kept local
 * (rather than reusing `isUrlOnDomain`) because both inputs here are hostnames,
 * not URLs, and re-normalizing per comparison would be wasted work inside the
 * hot loop the returned classifier runs.
 */
function hostMatches(host: string, target: string): boolean {
	return host === target || host.endsWith(`.${target}`);
}

/**
 * Build a classifier that says whether a `site:`-scoped domain is ours, a
 * tracked competitor's, or someone else's.
 *
 * PURE and context-injected by design. Classification is deliberately NOT
 * persisted (only the parse is — `GroundingSearch.siteScopeDomain`), because the
 * answer depends on the CURRENT competitor list: add a competitor today and
 * every historical `site:them.com` row reclassifies for free, with no backfill.
 * That only works if classification happens at read time, and it can only happen
 * at read time cheaply if the caller loads the property + competitors ONCE and
 * hands them in — hence the context parameter rather than a query in here.
 *
 * Matching is exact-or-subdomain in both directions of the "own" test:
 * `docs.acme.com` counts as own for `acme.com`, because a model consulting our
 * docs site is consulting us.
 *
 * Ordering matters: own wins over competitor. If a domain somehow appears in
 * both lists (a competitor row pointing at our own site, which happens with
 * mis-entered data), calling it ours is the safe read — it keeps us from
 * reporting our own authority as a competitor's.
 *
 * @example
 * const classify = buildSiteScopeClassifier({
 *   property: { domain: "acme.com" },
 *   competitors: [{ id: "c1", name: "Globex", url: "https://globex.com" }],
 * });
 * classify("docs.acme.com") // { class: "own" }
 * classify("globex.com")    // { class: "competitor", competitorId: "c1", competitorName: "Globex" }
 * classify("reddit.com")    // { class: "third_party" }
 */
export function buildSiteScopeClassifier(
	context: SiteScopeClassifierContext,
): SiteScopeClassifier {
	// `propertyFocalDomain` returns null (never "") for PERSON/CATEGORY subjects
	// and for a PRODUCT with an unparseable seedUrl. Filtering nulls out here is
	// what stops a domain-less property from matching the empty string and
	// tagging every consultation as its own.
	const ownDomains = [
		propertyFocalDomain(context.property),
		...(context.property.additionalDomains ?? []).map(
			normalizeDomainOrNull,
		),
	].filter((d): d is string => d !== null);

	const competitorDomains: Array<{
		domain: string;
		id: string;
		name: string | null;
	}> = [];
	for (const competitor of context.competitors ?? []) {
		// Peer "urls" (`peer:jane-doe`) normalize to something that can never
		// equal a real hostname, so they fall out naturally — but a null here
		// would match nothing anyway.
		const domain = normalizeDomainOrNull(competitor.url);
		if (!domain?.includes(".")) continue;
		competitorDomains.push({
			domain,
			id: competitor.id,
			name: competitor.name,
		});
	}

	return (raw) => {
		const host = normalizeDomainOrNull(raw);
		if (!host) return THIRD_PARTY;

		if (ownDomains.some((own) => hostMatches(host, own))) {
			return { class: "own" };
		}

		const competitor = competitorDomains.find((c) =>
			hostMatches(host, c.domain),
		);
		if (competitor) {
			return {
				class: "competitor",
				competitorId: competitor.id,
				competitorName: competitor.name ?? competitor.domain,
			};
		}

		return THIRD_PARTY;
	};
}

// ---------------------------------------------------------------------------
// Brand affinity — is this third-party domain one the model MISTOOK for ours?
// ---------------------------------------------------------------------------

/**
 * Second-level domains whose final TWO labels are the public suffix.
 *
 * This is a deliberately small hand-list, not the Public Suffix List. The PSL
 * is ~10k entries and a network-refreshed dependency; this module is pure and
 * ships to the browser, so it takes the 95% cut instead. The cost of a miss is
 * bounded and one-directional: an unlisted two-part suffix (say `co.ke`) makes
 * `acme.co.ke` yield the SLD "co" instead of "acme", which is 2 chars and
 * therefore fails every guard below — a missed alert, never a false one.
 */
const TWO_PART_PUBLIC_SUFFIXES = new Set([
	"co.uk",
	"org.uk",
	"ac.uk",
	"gov.uk",
	"me.uk",
	"net.uk",
	"com.au",
	"net.au",
	"org.au",
	"edu.au",
	"gov.au",
	"co.nz",
	"net.nz",
	"org.nz",
	"co.za",
	"org.za",
	"co.jp",
	"ne.jp",
	"or.jp",
	"ac.jp",
	"co.kr",
	"or.kr",
	"co.in",
	"net.in",
	"org.in",
	"co.il",
	"co.id",
	"co.th",
	"com.br",
	"net.br",
	"org.br",
	"com.cn",
	"net.cn",
	"org.cn",
	"com.mx",
	"com.ar",
	"com.co",
	"com.pe",
	"com.sg",
	"com.hk",
	"com.tw",
	"com.tr",
	"com.my",
	"com.ph",
	"com.vn",
	"com.pl",
	"com.ua",
	"com.es",
	"com.pt",
]);

/**
 * The shortest token this module will ever match on, in either direction.
 *
 * Below four characters a substring hit is noise rather than evidence: a brand
 * literally named "AI" or "Go" would otherwise mark `openai.com` and
 * `google.com` as impersonating it, and a two-character SLD like `g2` has a
 * single bigram, so its Dice score against anything is an artifact of length.
 * The guard applies to brand tokens AND to both sides of the similarity test.
 */
const MIN_AFFINITY_TOKEN_LENGTH = 4;

/** Dice score at or above which two SLDs are treated as the same word. */
const AFFINITY_SIMILARITY_THRESHOLD = 0.8;

/**
 * The registrable label of a hostname — everything before the public suffix.
 *
 * `www.acme.co.uk` → `acme`; `docs.acme.com` → `acme`; `acme.com` → `acme`.
 * Strips the final label, or the final two when they form a known two-part
 * suffix (see {@link TWO_PART_PUBLIC_SUFFIXES}). Comparing SLDs rather than
 * whole hostnames is the entire point: the wrong-domain case IS a right word
 * under a wrong suffix.
 */
function registrableLabel(host: string): string {
	const labels = host.split(".");
	if (labels.length <= 1) return host;

	const lastTwo = labels.slice(-2).join(".");
	const dropped = TWO_PART_PUBLIC_SUFFIXES.has(lastTwo) ? 3 : 2;
	return labels[labels.length - dropped] ?? "";
}

/**
 * Reduce a label or brand name to comparable letters: lowercase, alphanumerics
 * only. This is what lets `spy-glasses` meet `Spyglasses` — the hyphen a model
 * hallucinates (or a squatter buys) carries no meaning here.
 */
function affinityToken(value: string): string {
	return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function bigrams(value: string): string[] {
	const out: string[] = [];
	for (let i = 0; i < value.length - 1; i += 1) {
		out.push(value.slice(i, i + 2));
	}
	return out;
}

/**
 * Sørensen–Dice coefficient over character bigrams, 0–1.
 *
 * Chosen over edit distance because it is length-normalized and order-tolerant
 * in the way domain typos actually behave, and because it needs no matrix — it
 * runs inside a per-execution loop in the nightly detector.
 */
function diceCoefficient(a: string, b: string): number {
	if (a === b) return 1;

	const left = bigrams(a);
	const right = bigrams(b);
	if (left.length === 0 || right.length === 0) return 0;

	// Multiset intersection: a repeated bigram may only be consumed once, or
	// "aaaa" would score 1.0 against "aa".
	const remaining = new Map<string, number>();
	for (const gram of left) {
		remaining.set(gram, (remaining.get(gram) ?? 0) + 1);
	}

	let hits = 0;
	for (const gram of right) {
		const count = remaining.get(gram) ?? 0;
		if (count > 0) {
			remaining.set(gram, count - 1);
			hits += 1;
		}
	}

	return (2 * hits) / (left.length + right.length);
}

export interface BrandAffinityContext {
	/** Property name + aliases. Each is tested independently. */
	brandNames: Array<string | null | undefined>;
	/**
	 * The property's own domain, or null. A null focal domain is NORMAL, not a
	 * degenerate case: PERSON and CATEGORY subjects have no site of their own,
	 * and those are exactly the properties whose real brand site gets classified
	 * `third_party`. The brand-name path must therefore stand on its own.
	 */
	focalDomain: string | null;
}

/**
 * Does a third-party domain look like it was mistaken for the brand's own?
 *
 * The question this answers is narrow and worth stating precisely: the model
 * consulted a site that is NOT ours and NOT a tracked competitor's, and we want
 * to know whether it went there *thinking it was us*. `site:reddit.com` is a
 * model consulting a forum; `site:spyglasses.io` when we are `spyglasses.ai` is
 * a model handing our authority — and our citation — to a stranger.
 *
 * Two independent tests, either sufficient, both operating on the second-level
 * domain rather than the whole hostname (see {@link registrableLabel}):
 *
 *  1. **Brand token.** The SLD contains (or equals) a normalized brand name of
 *     at least {@link MIN_AFFINITY_TOKEN_LENGTH} characters. Catches prefixes
 *     and suffixes a squatter or a hallucination adds — `getspyglasses.com`,
 *     `spyglassesapp.com` — and works with no focal domain at all.
 *  2. **Focal similarity.** Dice bigram similarity between the SLD and the
 *     focal domain's SLD is at least {@link AFFINITY_SIMILARITY_THRESHOLD}.
 *     Catches the wrong-TLD case (`spyglasses.io` vs `spyglasses.ai`, score
 *     1.0) and near-misses the brand name alone would not, e.g. when the
 *     registered name and the company name have drifted apart.
 *
 * Deliberately conservative in both directions. Every match costs somebody an
 * alert about a domain they may have no control over, so the short-token guard
 * is absolute: a brand called "AI" or "Go" never matches anything, because at
 * that length "contains the brand name" stops being evidence of anything.
 *
 * @example
 * isBrandAffineDomain("spyglasses.io", { brandNames: [], focalDomain: "spyglasses.ai" })      // true
 * isBrandAffineDomain("getspyglasses.com", { brandNames: ["Spyglasses"], focalDomain: null }) // true
 * isBrandAffineDomain("spy-glasses.net", { brandNames: ["Spyglasses"], focalDomain: null })   // true
 * isBrandAffineDomain("reddit.com", { brandNames: ["Spyglasses"], focalDomain: "spyglasses.ai" }) // false
 * isBrandAffineDomain("openai.com", { brandNames: ["AI"], focalDomain: null })                // false
 */
export function isBrandAffineDomain(
	domain: string | null | undefined,
	context: BrandAffinityContext,
): boolean {
	const host = normalizeDomainOrNull(domain);
	if (!host) return false;

	const sld = affinityToken(registrableLabel(host));
	if (!sld) return false;

	for (const brandName of context.brandNames ?? []) {
		if (!brandName) continue;
		const token = affinityToken(brandName);
		if (token.length < MIN_AFFINITY_TOKEN_LENGTH) continue;
		if (sld.includes(token)) return true;
	}

	const focalHost = normalizeDomainOrNull(context.focalDomain);
	if (!focalHost) return false;

	const focalSld = affinityToken(registrableLabel(focalHost));
	if (
		sld.length < MIN_AFFINITY_TOKEN_LENGTH ||
		focalSld.length < MIN_AFFINITY_TOKEN_LENGTH
	) {
		return false;
	}

	return diceCoefficient(sld, focalSld) >= AFFINITY_SIMILARITY_THRESHOLD;
}

/**
 * How strongly a domain looks like a brand's, 0–1. Zero exactly when
 * {@link isBrandAffineDomain} is false; otherwise the larger of:
 *
 *  - **Brand-token coverage**: the share of the domain's SLD that a matching
 *    brand token covers (`spyglasses.io` vs "Spyglasses" → 1.0,
 *    `getspyglasses.com` → 10/13).
 *  - **Focal similarity**: the Dice score against the focal SLD, when it clears
 *    the affinity threshold.
 *
 * Both are "how much of this domain is explained by the brand", so comparing
 * one brand's strength against another's is meaningful — which is all the
 * competitor guard below needs.
 */
export function brandAffinityStrength(
	domain: string | null | undefined,
	context: BrandAffinityContext,
): number {
	const host = normalizeDomainOrNull(domain);
	if (!host) return 0;

	const sld = affinityToken(registrableLabel(host));
	if (!sld) return 0;

	let strength = 0;
	for (const brandName of context.brandNames ?? []) {
		if (!brandName) continue;
		const token = affinityToken(brandName);
		if (token.length < MIN_AFFINITY_TOKEN_LENGTH) continue;
		if (sld.includes(token)) {
			strength = Math.max(strength, token.length / sld.length);
		}
	}

	const focalHost = normalizeDomainOrNull(context.focalDomain);
	if (focalHost) {
		const focalSld = affinityToken(registrableLabel(focalHost));
		if (
			sld.length >= MIN_AFFINITY_TOKEN_LENGTH &&
			focalSld.length >= MIN_AFFINITY_TOKEN_LENGTH
		) {
			const dice = diceCoefficient(sld, focalSld);
			if (dice >= AFFINITY_SIMILARITY_THRESHOLD) {
				strength = Math.max(strength, dice);
			}
		}
	}

	return strength;
}

/** The competitor shape the lookalike guard needs — a subset of `Competitor`. */
export interface LookalikeCompetitorSource {
	name: string | null;
	url: string;
	aliases?: string[] | null;
	/** Inactive competitors are ignored. Missing = active. */
	active?: boolean | null;
}

function competitorAffinityContext(
	competitor: LookalikeCompetitorSource,
): BrandAffinityContext {
	const domain = normalizeDomainOrNull(competitor.url);
	return {
		brandNames: [competitor.name, ...(competitor.aliases ?? [])],
		// Peer "urls" (`peer:jane-doe`) are not hostnames; only a dotted host
		// can serve as a focal domain.
		focalDomain: domain?.includes(".") ? domain : null,
	};
}

/**
 * The strongest brand affinity a domain has to any ACTIVE competitor, 0–1.
 */
export function competitorLookalikeStrength(
	domain: string | null | undefined,
	competitors: LookalikeCompetitorSource[],
): number {
	let strength = 0;
	for (const competitor of competitors ?? []) {
		if (competitor.active === false) continue;
		strength = Math.max(
			strength,
			brandAffinityStrength(
				domain,
				competitorAffinityContext(competitor),
			),
		);
	}
	return strength;
}

/**
 * Does this domain look like an active competitor's (brand-affine to its name,
 * aliases or normalized url)?
 */
export function isCompetitorLookalike(
	domain: string | null | undefined,
	competitors: LookalikeCompetitorSource[],
): boolean {
	return competitorLookalikeStrength(domain, competitors) > 0;
}

export interface FirstPartyLookalikeContext extends BrandAffinityContext {
	competitors: LookalikeCompetitorSource[];
}

/**
 * Is this a FIRST-PARTY lookalike — a domain the model plausibly mistook for
 * the property's own site, and not a competitor's?
 *
 * True when the domain is brand-affine to the property AND no active
 * competitor's affinity is at least as strong. The tie goes to the competitor
 * on purpose: an investigation (and an email to the whole team) about a
 * domain that is equally likely to be a rival's is the expensive mistake.
 *
 * Callers still exclude own and competitor domains via
 * {@link buildSiteScopeClassifier} first; this guards the third-party
 * remainder.
 */
export function isFirstPartyLookalike(
	domain: string | null | undefined,
	context: FirstPartyLookalikeContext,
): boolean {
	const own = brandAffinityStrength(domain, context);
	if (own <= 0) return false;
	return competitorLookalikeStrength(domain, context.competitors) < own;
}
